// 190 §C — THE EISENHOWER CARD, moved out of EisenhowerDialog so the Bento over
// tasks renders EXACTLY the card the matrix renders (Laurent: "COPY that
// working exactly, don't reinvent").
//
// MOVED, AND TYPED AT ITS SEAM. The block below is the body of the
// `tasksByQ[q.k].map((task) => { … })` callback as it stood in
// EisenhowerDialog.tsx:273–484 — sha256 of the moved text
//   82aca765e25a949bcbc618d5d9c381e76c1518c5b8028fddb3136fb80c1effbb
// — with EXACTLY TWO EDITS inside it, both type annotations and neither markup:
// `cases.find((c: any) => …)` → `cases.find((c) => …)` and
// `tkTypes.find((x: any) => …)` → `tkTypes.find((x) => …)`. Every style, every
// element, every comment is the dialog's, indentation included. The seam is
// typed because a new src/ file carries ZERO warnings (186, the eslint ratchet
// refuses a new file with any) while the dialog's `any` props stay the
// dialog's: `Props` names the shapes the body reads, no more.
import React, { useMemo } from "react"; // react/react-in-jsx-scope is counted per JSX element (182 C.1)
import type { CSSProperties, Dispatch } from "react";
import { eisenQuadrant, edgeTint } from "../utils"; // 190 round 7 [AF]
import { EisenBadge, eisenLabel } from "../EisenBadge";
import DateBlock from "./DateBlock";
import TypeTagBox, { TYPE_CHIP } from "./TypeTagBox"; // 204 §C.0 — the type chip's one definition
import { taskUrgencyColor } from "../lib/taskUrgency";
import { dayDiff, dayCountdownLabel } from "../lib/dayCountdown";
import { longestTaxonomyCode } from "../utils/taxonomyMap";
import { FS, RAD, CARD_PAD } from "../lib/tokens"; // 205 §3 — the card's own padding, one name
import type { TFn } from "../lib/taxonomyTypes";
import LinkIcon from "./LinkIcon";
import { resolveMatter } from "../lib/matterLabel"; // 191 §C

/* 190 glance (i)(c) — THE CARD ALIGNS LIKE THE TASK LIST: fixed slots, and only
   the central fields adapt. Laurent: "Items in individual task panes are not
   aligned anymore like in tasks (fixed width columns except the central
   adapting/truncating ones)." MEASURED on staging before this: in one column,
   "ADMIN" and "TEL" chips of 49.8 and 35.8px put the badge at x 72 and x 58,
   a link icon present on one card and not the next moved the ● from 97 to
   125, and the case name's own width put the title at 237 and 152 — every
   card its own ruler.

   The slots are the task row's own (CaseTaskRow.tsx), one each:
     type   — the widest chip the task taxonomy can show, TypeTagBox's mirror
              (139 §1's ruling for a CARD; the table's 60 is its header's word)
              — or, where the host passes `typeMirror`, the widest code ON ITS
              BOARD (the two Bentos do: a case's board of untyped tasks was
              reserving 52 CSS px of nothing per card)
     badge  — 22, centred (the row's `width: 22, textAlign: "center"` cell)
     ●      — the manual mark's own glyph width, reserved whether or not it shows
     case name · title — the two central ones, A1c's 40% cap and A1b's 3:1
              shrink kept exactly; the link icon rides at the title's TAIL, where
              the task row keeps it (its title cell holds the icons after the
              text), so an icon on one card and not the next moves nothing
     days   — the widest label ON THE BOARD (the host's `daysMirror` — a table
              column is as wide as its widest cell, no wider) reserved by the
              same mirror, the real one at the slot's end; a host that passes
              none gets a content-sized label
     date   — DateBlock, its own width
   `gap` is the row's 4 (its cells' 2px + 2px), no longer the matrix's 8.
   MEASURED before this tightening, at 1600px/xlarge: with the day slot as
   wide as "Aujourd'hui" and the gap at 8, a 287 CSS px column left the title
   0px — the fixed slots and gaps alone were 303 of the card's 353. */
const BADGE_SLOT_W = 22;
const MANUAL_SLOT_W = 10;
/* 204 §C.0 — the type chip's style moved to TypeTagBox.tsx as `TYPE_CHIP`,
   byte-identical, so the Costs & Fees activity headers can wear the SAME chip
   (one definition, not a fifth copy); this card reads it from there. */
/* [E] — the central slot's box, one row (the matrix) or a column of two (a
   Bento); A1b's flex either way. A module-level function, so the choice does
   not sit inside the component's own complexity budget. */
const centralSlotStyle = (stacked: boolean): CSSProperties =>
  stacked
    ? { flex: "1 3 auto", minWidth: 0, display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "center", gap: 1, overflow: "hidden" }
    : { flex: "1 3 auto", minWidth: 0, display: "inline-flex", flexDirection: "row", alignItems: "center", gap: 4, overflow: "hidden" };
const DAYS: CSSProperties = {
  /* §5a — the agenda's own countdown size (AgendaView.tsx:623). */
  fontSize: FS.s13,
  /* BRIEF 104 §A2a — BOLD, and this is one of only TWO places the ruling
     changes anything. Size and colour untouched; weight only. See TaskMatrix
     for the census that says why. */
  fontWeight: 700,
  lineHeight: 1.3,
  whiteSpace: "nowrap",
};

// The same clock `taskUrgencyColor` reads, so a card's words and its colour
// cannot disagree. UTC, and known to be so — see src/lib/taskUrgency.ts.
const todayISO = () => new Date().toISOString().slice(0, 10);

/** What the card reads off a task — the union of the body's reads. */
export interface EisenCardTask {
  id: number;
  caseId?: number | null;
  ccId?: number | null;
  type?: string | null;
  /** `title` and `desc` feed DOM `title=` attributes, which take string | undefined. */
  title?: string;
  dueDate?: string | null;
  status?: string | null;
  assignedCoId?: number | null;
}
interface EisenCardCase { id: number; title?: string; itemLinks?: unknown }
/** 191 §C — the collective list carries its title too: a cc task's card names its matter. */
interface EisenCardCc { id: number; title?: string; itemLinks?: unknown }
interface EisenCardType { code?: string | null; desc?: string }

interface Props {
  readonly task: EisenCardTask;
  readonly cases: readonly EisenCardCase[];
  readonly tkTypes: readonly EisenCardType[];
  readonly collectiveCases?: readonly EisenCardCc[];
  /** task id → quadrant, the viewer's manual placements (a key present = manual). */
  readonly userMatrix: Readonly<Record<string, unknown>>;
  /** The matrix's drag state — the card dims while it is the one being dragged.
   *  OPTIONAL since the 190 glance, RULED (i)(a): NO DRAG-DIM IN A BENTO. The
   *  two Bento hosts pass neither, so nothing dims there; the matrix passes
   *  both and keeps its dim. MEASURED before the ruling: the Bento's copy of
   *  this state stuck at 0.4 after every cross-column drop — the optimistic
   *  placement re-parents the card in the commit that follows the drop, the
   *  `dragend` then fires on a DETACHED node and never reaches React's root,
   *  so the id was never cleared (the matrix is immune only because its
   *  quadrant's onDrop clears it itself, EisenhowerDialog.tsx:202). */
  readonly eisenDragId?: unknown;
  readonly setEisenDragId?: Dispatch<unknown>;
  readonly onCardClick: Dispatch<EisenCardTask>;
  readonly t: TFn;
  /** The hosting column's accent — `q.accent` in the body, the quadrant's colour. */
  readonly q: { accent: string };
  /** 190 glance (i)(c) — a case's own Bento shows the same case name on every
   *  card; it passes false and the central slot goes to the title. The link
   *  icon still resolves through the case row. Default true: the matrix and
   *  the user Bento mix matters. */
  readonly showMatter?: boolean;
  /** 190 glance (i)(c) — the widest day-counter label among the host's cards
   *  (the host knows every task; the card knows one), so the slot is one
   *  width down a column and no wider than the board needs. */
  readonly daysMirror?: string | null;
  /** 190 glance (i)(c) — the widest type code among the host's cards, same
   *  reason; `undefined` (the matrix) keeps the taxonomy-wide mirror, `""`
   *  reserves nothing. */
  readonly typeMirror?: string;
  /** 190 glance round 2 [E], RULED (Laurent's 4(a)): in a Bento the card is
   *  TWO ROWS — the case title above, the task title just under — sized to
   *  the date block's height so every card in a Bento is one height. The
   *  matrix keeps 104 §A1's one row. */
  readonly stacked?: boolean;
  /** [E]'s invariant — UNIFORM CARD HEIGHT WITHIN A BENTO: a card with no due
   *  date reserves the date block's height (and its slot) with a hidden
   *  block, so it stands as tall as its neighbours. The Bento hosts pass it;
   *  the matrix does not. */
  readonly reserveDate?: boolean;
}

export default function EisenTaskCard({
  task,
  cases,
  tkTypes,
  collectiveCases,
  userMatrix,
  eisenDragId,
  setEisenDragId,
  onCardClick,
  t,
  q,
  showMatter = true,
  daysMirror = null,
  typeMirror,
  stacked = false,
  reserveDate = false,
}: Props) {
                  /* 191 §C — the matter, through the one resolver: a collective-
                     case task names its collective case (it resolved nothing here). */
                  const cs2 = resolveMatter(task, cases, collectiveCases);
                  const tkt2 = tkTypes.find((x) => x.code === task.type);
                  /* (i)(c) — the type mirror: the longest task-type code the
                     tenant's non-archived taxonomy can show (as the event
                     surfaces' `evtLongestCode`, App.tsx:1311); re-derived only
                     when the taxonomy moves. */
                  const longestTaxonomy = useMemo(() => longestTaxonomyCode(tkTypes), [tkTypes]);
                  const longestType = typeMirror === undefined ? longestTaxonomy : typeMirror;
                  /* BRIEF 102 §2e — THE LEVEL LETTER, ITS LOOKUP AND THE COMMENT
                     THAT GUARDED IT ARE ALL GONE TOGETHER.
                     The comment that stood here described a crash — "`pri.c`
                     below throws. Not a wrong badge: a blank view" — reachable
                     only through `pri`, whose only two readers in this file were
                     the letter's colour and the letter itself. Keeping the
                     lookup would have left a line computing something nobody
                     reads under a comment that still read as load-bearing,
                     which is the worse of the two failures. `ITEM_LEVEL` and
                     `provisionalLevel` went unused with it and are off the
                     props and the imports; the caller in App.tsx no longer
                     passes the ladder to a dialog that does not draw levels. */
                  const isManual = !!userMatrix[task.id];
                  /* §2d — the urgency colour, from the ONE definition the task
                     list already uses (src/lib/taskUrgency.ts). */
                  const clr = taskUrgencyColor(task);
                  /* §2f — the Agenda list's day counter, from the ONE
                     definition it now shares with the event card. Same clock as
                     `taskUrgencyColor` above, so the words and the colour can
                     never disagree on the same card. */
                  const days = dayCountdownLabel(dayDiff(task.dueDate, todayISO()), t);
                  /* BRIEF 101 §3 — the block, per §0's ruling; §2a moved it
                     out of the tag strip and beside the whole column. [E] —
                     with no due date, a Bento (`reserveDate`) reserves the
                     block's height and slot with the block itself (today's
                     date, unseen), so its cards are one height either way. */
                  let dateSlot: React.ReactNode = null;
                  if (task.dueDate) {
                    /* BRIEF 104 §3a — the day carries the red, from the `clr`
                       this card already computed for its title. */
                    dateSlot = <DateBlock date={task.dueDate} dayColor={clr} style={{ flexShrink: 0 }} />;
                  } else if (reserveDate) {
                    dateSlot = <DateBlock date={todayISO()} style={{ flexShrink: 0, visibility: "hidden" }} />;
                  }
                  return (
                    <div
                      key={task.id}
                      data-lnk={"task-" + task.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", String(task.id));
                        e.dataTransfer.effectAllowed = "move";
                        setEisenDragId?.(task.id);
                      }}
                      onDragEnd={() => setEisenDragId?.(null)}
                      onClick={() => onCardClick(task)}
                      style={{
                        background: "var(--bc)",
                        border: "1px solid var(--br)",
                        /* 190 round 7 [AF] — the light tint, one derivation (utils). */
                        borderLeft: "3px solid " + edgeTint(q.accent),
                        borderRadius: RAD.r6,
                        padding: CARD_PAD,
                        cursor: "pointer",
                        /* §5a — the agenda row's own text size. */
                        fontSize: FS.s11_5,
                        opacity: eisenDragId === task.id ? 0.4 : 1,
                        boxShadow: "0 1px 3px rgba(0,0,0,.3)",
                        /* BRIEF 102 §2a — the cause was a PLACEMENT: a three-line
                           date block was mounted inside the card's one-line tag
                           strip, so the strip grew to the block's height and the
                           card wore it. That fix stays; the block is a sibling.
                           BRIEF 104 §A1 — AND THE EARLIER READING WAS TOO WEAK.
                           102 read "everything should be in ONE ROW" as "the card
                           reads as one row with the date beside it" and said so;
                           it was agreed on a description rather than on the
                           screen, and the screen settled it — three lines of text
                           beside a date block is not one row. THE STRONG READING
                           IS THE CORRECT ONE. Every child is now a sibling on one
                           flex line, in A1a's order:
                             [type] [matrix icon] [●] [case name] [title] ……… [days] [date]
                           `overflow: hidden` is not decoration — it is A1b's
                           floor. A one-line card whose title overflows its
                           quadrant is worse than the three-line card, so the card
                           refuses to be pushed wider than its quadrant even if a
                           child mis-measures. */
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        overflow: "hidden",
                      }}
                    >
                      {/* (i)(c) — the type slot: the chip inside the box that
                          reserves the widest code's width; a task with no type
                          keeps the slot empty, as the row's cell keeps its "—". */}
                      <TypeTagBox
                        mirror={longestType ? <span style={TYPE_CHIP}>{longestType}</span> : null}
                      >
                        {tkt2 && (
                          <span title={tkt2.desc} style={TYPE_CHIP}>
                            {task.type}
                          </span>
                        )}
                      </TypeTagBox>
                      {/* (i)(c) — the badge slot: the row's 22px centred cell. */}
                      <span
                        style={{
                          width: BADGE_SLOT_W,
                          flexShrink: 0,
                          display: "inline-flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        <EisenBadge
                          q={eisenQuadrant(task)}
                          /* §5a — `CaseTaskRow`'s own non-compact badge size. */
                          size={12}
                          title={
                            eisenLabel(eisenQuadrant(task)) +
                            (isManual ? " (manual)" : " (auto)")
                          }
                        />
                      </span>
                      {/* (i)(c) — the manual mark's slot, reserved either way. */}
                      <span
                        style={{
                          width: MANUAL_SLOT_W,
                          flexShrink: 0,
                          display: "inline-flex",
                          justifyContent: "center",
                          fontSize: FS.s9_5,
                          color: "var(--ac)",
                        }}
                        title={
                          isManual
                            ? t("eisen.manually_placed", {
                                fallback: "Manually placed",
                              })
                            : undefined
                        }
                      >
                        {isManual ? "●" : ""}
                      </span>
                      {/* BRIEF 104 §A1c — THE CASE NAME GIVES WAY SECOND. It keeps
                          its own colour, its `title` and its own ellipsis; what it
                          gains is `flexShrink: 1` against the title's 3, and a
                          `maxWidth` so it can never take the line on its own. The
                          order is stated rather than emergent: the two adaptive
                          fields shrink in proportion to base × shrink-factor, so
                          at 3:1 the TITLE loses three pixels for every one the
                          case name loses, and the case name only starts to
                          disappear once the title has nearly gone. */}
                      {showMatter && cs2 && !stacked && (
                        <span
                          style={{
                            flex: "0 1 auto",
                            minWidth: 0,
                            maxWidth: "40%",
                            fontSize: FS.s9_5,
                            color: "var(--ac)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={cs2.title}
                        >
                          {cs2.title}
                        </span>
                      )}
                      {/* §A1b — THE TITLE IS THE ADAPTIVE PART. `flex: 1 3 auto`
                          is the whole of A1a's "………": it GROWS into whatever the
                          fixed children leave, and it SHRINKS first when there is
                          nothing to leave.
                          102 §1c applies in full and is satisfied here rather than
                          assumed: `minWidth: 0` is what lets a flex item go below
                          its content at all, and overflow / ellipsis / nowrap are
                          what make the result an ellipsis instead of an overflow.
                          The card itself is `overflow: hidden`, so even a browser
                          that disagreed about the flex arithmetic cannot push a
                          quadrant sideways. */}
                      {/* (i)(c) — the title slot carries A1b's flex and holds
                          the title AND, at its tail, the link icon (111 §5c;
                          the task row's own placement — CaseTaskRow.tsx:1290
                          keeps it after the text in the title cell).
                          Round 2 [E] — STACKED (a Bento): the same slot is a
                          COLUMN, the case title on its own line above the
                          title line, each ellipsising on its own; the case
                          name no longer competes with the title for one line. */}
                      <span style={centralSlotStyle(stacked)}>
                        {stacked && showMatter && cs2 && (
                          <span
                            style={{
                              minWidth: 0,
                              fontSize: FS.s9_5,
                              color: "var(--ac)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={cs2.title}
                          >
                            {cs2.title}
                          </span>
                        )}
                        <span
                          style={{
                            minWidth: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            overflow: "hidden",
                          }}
                        >
                          <span
                            style={{
                              flex: "0 1 auto",
                              minWidth: 0,
                              /* §5a — the agenda row's title size. */
                              fontSize: FS.s11_5,
                              /* 102 §2d — the urgency colour, unchanged. */
                              color: clr || "var(--tp)",
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={task.title}
                          >
                            {task.title}
                          </span>
                          {/* 111 §5c — the Link icon; in-place highlight, no
                              scroll assist (v-f). */}
                          <LinkIcon
                            matterRow={cs2 /* 191 §C — the same resolved row */}
                            item={{ k: "task", id: task.id }}
                            scroll={false}
                          />
                        </span>
                      </span>
                      {/* 102 §2f — the day counter, now in A1a's place: after the
                          title and before the block, `flexShrink: 0`, so it is
                          never the thing that disappears. (i)(c): in a slot as
                          wide as the widest label, the number at its end. */}
                      <TypeTagBox align="end" mirror={daysMirror ? <span style={DAYS}>{daysMirror}</span> : null}>
                        {days && (
                          <span style={{ ...DAYS, color: clr || "var(--tm)" }}>
                            {days}
                          </span>
                        )}
                      </TypeTagBox>
                      {dateSlot}
                    </div>
                  );
}
